import { api, type VerticalInfo } from '@lipaflow/shared'
import type { ActivationStatus } from '@lipaflow/ui'

// === Types ===

export interface Tenant {
  id: string
  vertical_id?: string
  vertical_code?: string | null
  name: string
  short_name: string
  logo_url: string
  brand_color: string
  created_at: string
  is_active: boolean
  outbound_enabled: boolean
  ai_enabled: boolean
}

/** What a school can buy (education strategy), priced at its count: the
 *  same catalogue the school sees on its billing page. Empty where the
 *  vertical sells one thing. */
export interface TenantProducts {
  product: string | null
  pending_product: string | null
  billing_started: boolean
  count: number
  unit_label: string
  period: string
  products: { code: string; name: string; summary: string; per_period: number | null; currency: string }[]
}

/** What changing a tenant's product did. */
export interface ProductChangeResult {
  product_code: string
  when: 'now' | 'now_charged' | 'next_period' | 'unchanged'
  effective_from: string | null
  amount: number
  invoice_number: string | null
}

export interface TenantDetail {
  id: string
  vertical_id: string
  name: string
  short_name: string
  description: string | null
  email: string | null
  phone: string | null
  address: string | null
  logo_url: string | null
  brand_color: string | null
  paybill_number: string | null
  paybill_account_format: string | null
  bank_name: string | null
  bank_account: string | null
  whatsapp_phone_number_id: string | null
  settings: Record<string, unknown>
  is_active: boolean
  outbound_enabled: boolean
  ai_enabled: boolean
  ai_token_budget_monthly: number
  ai_tokens_used_this_month: number
  ai_budget_remaining: number
  vertical_code: string | null
  vertical_config: Record<string, unknown> | null
  operating_model: OperatingModel | null
  created_at: string
  updated_at: string
}

export interface TenantUpdatePayload {
  name?: string
  short_name?: string
  description?: string | null
  email?: string | null
  phone?: string | null
  address?: string | null
  logo_url?: string | null
  brand_color?: string | null
  ai_enabled?: boolean
  outbound_enabled?: boolean
}

export interface TenantUserSummary {
  id: string
  email: string
  full_name: string
  /** A display label only; never decide anything with it. */
  role: string
  /** An unscoped administrator of the customer: whom support access opens as. */
  is_administrator: boolean
  is_active: boolean
  is_verified: boolean
}

export interface SupportSessionTargetUser {
  id: string
  email: string
  full_name: string
  role: string
}

export interface SupportSessionResponse {
  access_token: string
  tenant_id: string
  tenant_name: string
  target_user: SupportSessionTargetUser
  session_type: 'sudo_user' | 'support_backdoor'
  launch_url: string
  impersonating: boolean
}

export interface OnboardingStats {
  total: number
  completed: number
  in_progress: number
  by_status: Record<string, number>
}

export interface PendingOnboarding {
  tenant_id: string
  tenant_name: string | null
  status: string
  current_step: string
  is_admin_setup: boolean
  assigned_to: string | null
  started_at: string | null
}

export interface StalledOnboarding {
  tenant_id: string
  tenant_name: string | null
  status: string
  current_step: string
  reminders_sent: number
  last_reminder_sent_at: string | null
}

export interface CompletedOnboarding {
  tenant_id: string
  tenant_name: string | null
  status: string
  current_step: string | null
  completed_at: string | null
  started_at: string | null
}

export interface Discount {
  id: string
  code: string
  description: string
  discount_type: string
  value: number
  max_uses: number | null
  max_uses_per_tenant: number
  min_seats: number | null
  min_amount: number | null
  max_discount_amount: number | null
  valid_from: string
  valid_until: string | null
  billing_cycles: string | null
  status: string
  times_used: number
  created_at: string
  updated_at: string
}

export interface DiscountCreatePayload {
  code: string
  description: string
  discount_type: string
  value: number
  max_uses?: number | null
  max_uses_per_tenant?: number
  min_seats?: number | null
  min_amount?: number | null
  max_discount_amount?: number | null
  valid_from?: string | null
  valid_until?: string | null
  billing_cycles?: string | null
}

export interface DiscountUpdatePayload {
  description?: string
  max_uses?: number | null
  max_discount_amount?: number | null
  valid_until?: string | null
  status?: string
}

export interface TrialPipelineEntry {
  tenant_id: string
  tenant_name: string
  trial_type: 'demo' | 'institutional' | null
  status: string
  trial_ends_at: string | null
  /** Null while in setup: the trial's days start at go-live. */
  trial_days_remaining: number | null
  trial_started?: boolean
  assigned_to: string | null
  created_at: string
  vertical_code: string | null
  admin_email: string | null
  /** Follow-up notes, newest first. */
  notes: TrialNote[]
}

export interface StaffMember {
  id: string
  full_name: string
  email: string
}

export interface TrialNote {
  date: string
  /** A LipaFlow staff user id. */
  author: string
  note: string
}

/** A trial's first invoice as it would be raised now (first_invoice.build_quote). */
export interface FirstInvoiceQuote {
  count: number
  unit_label: string
  band: string | null
  rate: number | null
  contract: boolean
  /** What this quote is for: the sector's own cycle, or "yearly". */
  cycle: string
  base_cycle: string
  cycles: string[]
  /** Periods covered: 1, or a year's worth up front. */
  periods: number
  period_start: string
  period_end: string
  days_covered: number
  period_days: number
  /** Null: the count is above every band and needs a contract rate. */
  amount: number | null
  currency: string
  needs_contract: boolean
  lines: { kind: string; description: string; amount: number }[]
}

export interface FirstInvoiceSummary {
  id: string
  number: string
  amount: number
  paid_amount: number
  status: string
  cycle: string | null
  service_period_end: string | null
  due_date: string | null
}

/** A customer's subscription as the renewal engine sees it. */
export interface CustomerSubscription {
  /** How the period running now was bought: the sector's own cycle, or 'yearly'. */
  current_cycle?: string
  status: string
  is_trial: boolean
  trial_ends_at: string | null
  licences_held: number | null
  licences_in_use: number
  plan: string
  plans: string[]
  period: string
  paid_until: string | null
  covered_until: string | null
  ends_at: string | null
  days_left: number | null
  notice_days: number
  renews_at: string | null
  showing: boolean
  cancelling: boolean
  invoice: {
    id: string
    number: string
    amount: number
    remaining: number
    status: string
    cycle: string | null
    seats: number
    starts: string | null
    ends: string | null
    due_date: string | null
  } | null
}

export interface BillingHealthIssue {
  kind: 'unpriced' | 'calendar_missing' | 'renewal_not_raised' | 'overdue'
  severity: 'critical' | 'warning' | 'info'
  tenant_id: string | null
  tenant_name: string | null
  detail: string
}

export interface BillingHealth {
  issues: BillingHealthIssue[]
  renewing_soon: {
    tenant_id: string
    tenant_name: string
    plan: string
    ends_at: string | null
    days_left: number | null
    cancelling: boolean
    invoice: { number: string; amount: number; status: string; due_date: string | null } | null
  }[]
  checked_at: string
}

export interface FirstInvoicePreview {
  quote: FirstInvoiceQuote
  /** The first invoice already raised and not yet paid, if any. */
  invoice: FirstInvoiceSummary | null
}

export interface IntegrationConfig {
  id: string
  tenant_id: string
  provider: string
  display_name: string
  status: string
  client_id_masked: string
  has_client_secret: boolean
  has_api_key: boolean
  base_url: string
  token_url: string
  config: Record<string, unknown>
  allowed_ips: string[]
  capabilities: string[]
  has_webhook_secret: boolean
  has_signing_key?: boolean
  /** Equity only: the IPN address to register in JengaHQ. */
  ipn_url?: string | null
  last_health_check: string | null
  last_health_status: string | null
  created_at: string
  updated_at: string
}

export interface IntegrationListResponse {
  items: IntegrationConfig[]
  total: number
  page: number
  per_page: number
  pages: number
}

export interface IntegrationCreatePayload {
  provider: string
  display_name: string
  client_id: string
  client_secret: string
  api_key?: string | null
  base_url: string
  token_url: string
  config: Record<string, unknown>
  webhook_secret?: string | null
  signing_key?: string | null
  allowed_ips: string[]
  capabilities: string[]
  status: string
}

export interface IntegrationUpdatePayload {
  display_name?: string
  status?: string
  client_id?: string
  client_secret?: string
  api_key?: string | null
  base_url?: string
  token_url?: string
  config?: Record<string, unknown>
  webhook_secret?: string | null
  signing_key?: string | null
  allowed_ips?: string[]
  capabilities?: string[]
}

export interface IntegrationTestPayload {
  test_phone?: string
  test_amount?: number
}

export interface IntegrationTestResult {
  success: boolean
  auth_status: string
  auth_error: string | null
  stk_push_status: string | null
  stk_push_error: string | null
  latency_ms: number | null
}

export interface Role {
  id: string
  code: string
  display_name: string
  vertical_code: string | null
  tenant_id: string | null
  required_scope: string | null
  template_version: number
  is_system: boolean
  is_active: boolean
  permissions: string[]
  available_permissions: string[]
}

export interface RoleCreatePayload {
  code: string
  display_name: string
  tenant_id: string
  required_scope?: string | null
}

export interface RoleUpdatePayload {
  display_name?: string
  is_active?: boolean
}

export interface VerticalConfigPatchResponse {
  vertical_id: string
  config: Record<string, unknown>
}

export interface CurriculumDocumentCreatePayload {
  curriculum_code: string
  title: string
  source_uri: string
  is_global: boolean
  tenant_id?: string | null
  checksum?: string | null
}

export interface CurriculumDocument {
  id: string
  tenant_id: string | null
  curriculum_code: string
  title: string
  source_uri: string
  checksum: string | null
  is_global: boolean
  status: string
  uploaded_by_user_id: string | null
  extra_data: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface CurriculumEditionCreatePayload {
  curriculum_code: string
  version_tag: string
  title: string
  published_year?: number | null
  locale?: string
}

export interface CurriculumEdition {
  id: string
  curriculum_code: string
  version_tag: string
  title: string
  status: string
  published_year: number | null
  locale: string
}

export interface CurriculumExtractionPayload {
  document_id: string
  edition_id: string
  model_name?: string | null
  subject_hint?: string | null
}

export interface CurriculumExtractionSummary {
  learning_areas: number
  strands: number
  sub_strands: number
  learning_outcomes: number
  activities: number
  competencies: number
  metadata_records: number
  rubrics: number
  rubric_indicators: number
  rubric_levels: number
  csl_templates: number
}

export interface CurriculumActivationResult {
  edition_id: string
  status: string
  outcomes: number
  embeddings_created: number
}

export interface CurriculumPrerequisiteInferPayload {
  edition_id: string
  target_edition_id?: string | null
  model_name?: string | null
}

export interface CurriculumPrerequisiteInferResult {
  edition_id: string
  target_edition_id: string | null
  edges_created: number
  edges_skipped: number
  total_candidates: number
}

// --- Curriculum Graph Viewer Types ---

export interface CurriculumSubStrandSummary {
  id: string
  code: string
  number: string
  name: string
  suggested_lessons: number
  default_term: number | null
  sort_order: number
}

export interface CurriculumStrandSummary {
  id: string
  code: string
  number: string
  name: string
  sort_order: number
  sub_strands: CurriculumSubStrandSummary[]
}

export interface CurriculumLearningAreaSummary {
  id: string
  code: string
  name: string
  grade: string
  lessons_per_week: number
  sort_order: number
  strands: CurriculumStrandSummary[]
}

export interface CurriculumEditionTreeResponse {
  edition_id: string
  curriculum_code: string
  version_tag: string
  status: string
  learning_areas: CurriculumLearningAreaSummary[]
}

export interface CurriculumActivityDetail {
  id: string
  description: string
  activity_type: string
  resources_needed: string[]
  sort_order: number
}

export interface CurriculumOutcomeDetail {
  id: string
  code: string
  letter: string
  outcome_text: string
  bloom_level: string | null
  difficulty: number | null
  source_page: number | null
  source_excerpt: string | null
  sort_order: number
  activities: CurriculumActivityDetail[]
}

export interface CurriculumCompetencyDetail {
  id: string
  competency_type: string
  description: string
}

export interface CurriculumRubricLevelDetail {
  level: string
  level_order: number
  description: string
  min_percentage: number
}

export interface CurriculumRubricIndicatorDetail {
  id: string
  indicator_text: string
  sort_order: number
  levels: CurriculumRubricLevelDetail[]
}

export interface CurriculumSubStrandDetailResponse {
  id: string
  code: string
  number: string
  name: string
  suggested_lessons: number
  suggested_minutes: number
  default_term: number | null
  sort_order: number
  strand_name: string
  learning_area_name: string
  learning_area_grade: string
  outcomes: CurriculumOutcomeDetail[]
  competencies: CurriculumCompetencyDetail[]
  rubric_indicators: CurriculumRubricIndicatorDetail[]
  metadata: {
    key_inquiry_questions: string[]
    cross_subject_links: string[]
    assessment_methods: string[]
    learning_resources: string[]
    non_formal_activities: string[]
  } | null
}

export interface CurriculumEditionStatsResponse {
  edition_id: string
  learning_areas: number
  strands: number
  sub_strands: number
  learning_outcomes: number
  activities: number
  competencies: number
  metadata_records: number
  rubrics: number
  rubric_indicators: number
  rubric_levels: number
  prerequisite_edges: number
  embeddings: number
}

export interface CurriculumBatchExtractionItem {
  document_id: string
  edition_id: string
  subject_hint?: string | null
}

export interface CurriculumBatchExtractionPayload {
  items: CurriculumBatchExtractionItem[]
  model_name?: string | null
}

export interface CurriculumBatchExtractionItemResult {
  document_id: string
  edition_id: string
  subject_hint: string | null
  status: string
  stats: Record<string, number> | null
  error: string | null
}

export interface CurriculumBatchExtractionResponse {
  total: number
  succeeded: number
  failed: number
  results: CurriculumBatchExtractionItemResult[]
}

export interface PlatformAuditLogItem {
  id: string
  tenant_id: string | null
  tenant_name: string | null
  user_id: string | null
  user_name: string | null
  actor_type: string
  action: string
  resource_type: string
  resource_id: string | null
  description: string | null
  request_id: string | null
  extra_data: Record<string, unknown>
  created_at: string
}

export interface PlatformAuditLogResponse {
  items: PlatformAuditLogItem[]
  total: number
  page: number
  per_page: number
  pages: number
}

export interface PlatformAuditLogFilters {
  /** A sector's activity; without it, platform activity (at no customer). */
  vertical_code?: string
  page?: number
  per_page?: number
  action?: string
  actor_type?: string
  resource_type?: string
  tenant_id?: string
  user_id?: string
  search?: string
  date_from?: string
  date_to?: string
}

function authHeaders(authToken?: string): Record<string, string> | undefined {
  if (!authToken) return undefined
  return { Authorization: `Bearer ${authToken}` }
}

// === Admin Service ===

export const adminService = {
  /** One page of a sector's customers, by name. */
  async listTenantsPage(
    sector: string,
    params?: { skip?: number; limit?: number; search?: string },
  ): Promise<Tenant[]> {
    const res = await api.get<Tenant[]>('/admin/tenants', {
      params: {
        vertical_code: sector,
        skip: params?.skip ?? 0,
        limit: params?.limit ?? 100,
        search: params?.search || undefined,
      },
    })
    return res.data
  },

  /** All of a sector's customers, by name. */
  async listTenants(sector: string, search?: string): Promise<Tenant[]> {
    const batchSize = 100
    const allTenants: Tenant[] = []
    let skip = 0
    let hasMore = true

    while (hasMore) {
      const page = await this.listTenantsPage(sector, { skip, limit: batchSize, search })
      allTenants.push(...page)

      // Stop when we got fewer results than requested or hit safety limit.
      hasMore = page.length >= batchSize && skip < 10000
      skip += batchSize
    }

    return allTenants
  },

  async getTenant(id: string): Promise<TenantDetail> {
    const res = await api.get<TenantDetail>(`/admin/tenants/${id}`)
    return res.data
  },

  /** The products a tenant's vertical sells and the one it has. */
  async getProduct(id: string): Promise<TenantProducts> {
    return (await api.get<TenantProducts>(`/admin/tenants/${id}/product`)).data
  },

  /** Ops change a tenant's product on its behalf: the same rules as the
   *  tenant's own change (free before billing; bigger now, charged; smaller
   *  next period). Audited with the reason. */
  async setProduct(id: string, productCode: string, reason: string): Promise<ProductChangeResult> {
    const res = await api.put<ProductChangeResult>(`/admin/tenants/${id}/product`, { product_code: productCode, reason })
    return res.data
  },

  /** Ops-only: set school / owner-managed / agency. Audited with the reason. */
  async setOperatingModel(
    id: string,
    operatingModel: OperatingModel,
    reason: string,
  ): Promise<TenantDetail> {
    const res = await api.put<TenantDetail>(`/admin/tenants/${id}/operating-model`, {
      operating_model: operatingModel,
      reason,
    })
    return res.data
  },

  async updateTenant(id: string, data: TenantUpdatePayload): Promise<TenantDetail> {
    const res = await api.patch<TenantDetail>(`/admin/tenants/${id}`, data)
    return res.data
  },

  async suspendTenant(id: string): Promise<{ status: string; tenant_id: string }> {
    const res = await api.post<{ status: string; tenant_id: string }>(`/admin/tenants/${id}/suspend`)
    return res.data
  },

  async activateTenant(id: string): Promise<{ status: string; tenant_id: string }> {
    const res = await api.post<{ status: string; tenant_id: string }>(`/admin/tenants/${id}/activate`)
    return res.data
  },

  /** Email a customer's administrator a fresh invitation. The link goes only to
   * their inbox; this returns the delivery, never the link. Resending voids the
   * earlier link. */
  async resendAdminInvitation(tenantId: string, email: string): Promise<{
    email: string; invite_sent: boolean; delivery_status: string; delivery_error: string | null
  }> {
    return (await api.post(`/admin/tenants/${tenantId}/admin-invitation/resend`, { email })).data
  },

  async listTenantUsers(id: string): Promise<TenantUserSummary[]> {
    const res = await api.get<TenantUserSummary[]>(`/admin/tenants/${id}/users`)
    return res.data
  },

  async startUserSudo(tenantId: string, targetUserId: string): Promise<SupportSessionResponse> {
    const res = await api.post<SupportSessionResponse>(`/admin/impersonate/${tenantId}`, {
      target_user_id: targetUserId,
    })
    return res.data
  },

  async startSupportAccess(tenantId: string, adminUserId?: string): Promise<SupportSessionResponse> {
    const payload = adminUserId ? { admin_user_id: adminUserId } : undefined
    const res = await api.post<SupportSessionResponse>(`/admin/tenants/${tenantId}/support-access`, payload)
    return res.data
  },

  async stopImpersonation(): Promise<{ message: string }> {
    const res = await api.post<{ message: string }>('/admin/stop-impersonation')
    return res.data
  },

  // === Integrations ===

  async listIntegrations(
    filters?: {
      provider?: string
      status?: string
      page?: number
      per_page?: number
    },
    authToken?: string,
  ): Promise<IntegrationListResponse> {
    const params = new URLSearchParams()
    if (filters?.provider) params.append('provider', filters.provider)
    if (filters?.status) params.append('status', filters.status)
    if (filters?.page) params.append('page', filters.page.toString())
    if (filters?.per_page) params.append('per_page', filters.per_page.toString())
    const suffix = params.toString() ? `?${params.toString()}` : ''
    const res = await api.get<IntegrationListResponse>(`/integrations${suffix}`, {
      headers: authHeaders(authToken),
    })
    return res.data
  },

  async createIntegration(
    payload: IntegrationCreatePayload,
    authToken?: string,
  ): Promise<IntegrationConfig> {
    const res = await api.post<IntegrationConfig>('/integrations', payload, {
      headers: authHeaders(authToken),
    })
    return res.data
  },

  async getIntegration(configId: string, authToken?: string): Promise<IntegrationConfig> {
    const res = await api.get<IntegrationConfig>(`/integrations/${configId}`, {
      headers: authHeaders(authToken),
    })
    return res.data
  },

  async updateIntegration(
    configId: string,
    payload: IntegrationUpdatePayload,
    authToken?: string,
  ): Promise<IntegrationConfig> {
    const res = await api.patch<IntegrationConfig>(`/integrations/${configId}`, payload, {
      headers: authHeaders(authToken),
    })
    return res.data
  },

  async testIntegration(
    configId: string,
    payload?: IntegrationTestPayload,
    authToken?: string,
  ): Promise<IntegrationTestResult> {
    const res = await api.post<IntegrationTestResult>(`/integrations/${configId}/test`, payload, {
      headers: authHeaders(authToken),
    })
    return res.data
  },

  /** Tell Safaricom where a till's or paybill's payments go (C2B) and register it for Pull Transactions. */
  async registerMpesa(configId: string, authToken?: string): Promise<{ c2b: string | null; pull: string | null }> {
    const res = await api.post(`/integrations/${configId}/mpesa/register`, null, { headers: authHeaders(authToken) })
    return res.data
  },

  async deactivateIntegration(configId: string, authToken?: string): Promise<void> {
    await api.delete(`/integrations/${configId}`, {
      headers: authHeaders(authToken),
    })
  },

  async listPlatformAuditLogs(filters?: PlatformAuditLogFilters): Promise<PlatformAuditLogResponse> {
    const res = await api.get<PlatformAuditLogResponse>('/admin/audit-logs', {
      params: filters,
    })
    return res.data
  },

  // === Roles ===

  async listRoles(filters?: {
    vertical_code?: string
    include_inactive?: boolean
  }): Promise<Role[]> {
    const params = new URLSearchParams()
    if (filters?.vertical_code) params.append('vertical_code', filters.vertical_code)
    if (filters?.include_inactive) params.append('include_inactive', 'true')
    const suffix = params.toString() ? `?${params.toString()}` : ''
    const res = await api.get<Role[]>(`/roles${suffix}`)
    return res.data
  },

  async createRole(payload: RoleCreatePayload): Promise<Role> {
    const res = await api.post<Role>('/roles', payload)
    return res.data
  },

  async getRole(roleId: string): Promise<Role> {
    const res = await api.get<Role>(`/roles/${roleId}`)
    return res.data
  },

  async updateRole(roleId: string, payload: RoleUpdatePayload): Promise<Role> {
    const res = await api.patch<Role>(`/roles/${roleId}`, payload)
    return res.data
  },

  async setRolePermissions(roleId: string, permissions: string[]): Promise<Role> {
    const res = await api.put<Role>(`/roles/${roleId}/permissions`, { permissions })
    return res.data
  },

  // === Verticals ===

  async listVerticals(): Promise<VerticalInfo[]> {
    const res = await api.get<VerticalInfo[]>('/admin/verticals')
    return res.data
  },

  async patchVerticalConfig(
    verticalId: string,
    config: Record<string, unknown>
  ): Promise<VerticalConfigPatchResponse> {
    const res = await api.patch<VerticalConfigPatchResponse>(`/admin/verticals/${verticalId}/config`, { config })
    return res.data
  },

  async getOnboardingStats(sector: string): Promise<OnboardingStats> {
    const res = await api.get<OnboardingStats>('/admin/onboarding/stats', { params: { vertical_code: sector } })
    return res.data
  },

  async getPendingOnboardings(sector: string, assignedTo?: string): Promise<PendingOnboarding[]> {
    const params = new URLSearchParams({ vertical_code: sector })
    if (assignedTo) params.append('assigned_to', assignedTo)
    const res = await api.get<PendingOnboarding[]>(`/admin/onboarding/pending?${params}`)
    return res.data
  },

  async getStalledOnboardings(sector: string, daysInactive?: number): Promise<StalledOnboarding[]> {
    const params = new URLSearchParams({ vertical_code: sector })
    if (daysInactive) params.append('days_inactive', daysInactive.toString())
    const res = await api.get<StalledOnboarding[]>(`/admin/onboarding/stalled?${params}`)
    return res.data
  },

  async getCompletedOnboardings(sector: string): Promise<CompletedOnboarding[]> {
    const res = await api.get<CompletedOnboarding[]>('/admin/onboarding/completed', { params: { vertical_code: sector } })
    return res.data
  },

  /** The customer's activation status, as the customer sees it plus LipaFlow's next steps (R15). */
  async getActivation(tenantId: string): Promise<ActivationStatus> {
    const res = await api.get<ActivationStatus>(`/admin/onboarding/${tenantId}/activation`)
    return res.data
  },

  async assignOnboarding(tenantId: string, staffUserId: string): Promise<{ status: string }> {
    const res = await api.post(`/admin/onboarding/${tenantId}/assign?staff_user_id=${staffUserId}`)
    return res.data
  },

  async rewindWizardStep(
    tenantId: string,
    step: string,
    reason?: string
  ): Promise<{ status: 'rewound'; current_step: string; removed_steps: string[] }> {
    const res = await api.post<{ status: 'rewound'; current_step: string; removed_steps: string[] }>(
      '/admin/wizard/rewind-step',
      {
        tenant_id: tenantId,
        step,
        reason,
      }
    )
    return res.data
  },

  // === Trial Pipeline ===

  async getTrialPipeline(sector: string, filters?: {
    trial_type?: string
    status?: string
    assigned_to?: string
  }): Promise<TrialPipelineEntry[]> {
    const params = new URLSearchParams({ vertical_code: sector })
    if (filters?.trial_type) params.append('trial_type', filters.trial_type)
    if (filters?.status) params.append('status', filters.status)
    if (filters?.assigned_to) params.append('assigned_to', filters.assigned_to)
    const res = await api.get<TrialPipelineEntry[]>(`/admin/trials/pipeline?${params}`)
    return res.data
  },

  /** Active LipaFlow staff, for the assign pickers (was a pasted user ID). */
  async listStaff(): Promise<StaffMember[]> {
    const res = await api.get('/admin/staff')
    return res.data
  },

  async assignTrialRep(tenantId: string, staffUserId: string): Promise<{ status: string }> {
    const res = await api.post(`/admin/trials/${tenantId}/assign`, { staff_user_id: staffUserId })
    return res.data
  },

  async addTrialNote(tenantId: string, note: string): Promise<{ status: string }> {
    const res = await api.post(`/admin/trials/${tenantId}/note`, { note })
    return res.data
  },

  async getFirstInvoice(tenantId: string, cycle?: string): Promise<FirstInvoicePreview> {
    return (await api.get<FirstInvoicePreview>(`/admin/tenants/${tenantId}/first-invoice`, {
      params: cycle ? { cycle } : undefined,
    })).data
  },

  /** Raise it in the chosen cycle; the other cycle replaces an unpaid one. */
  async raiseFirstInvoice(tenantId: string, cycle?: string): Promise<{ invoice: FirstInvoiceSummary }> {
    return (await api.post<{ invoice: FirstInvoiceSummary }>(`/admin/tenants/${tenantId}/first-invoice`, cycle ? { cycle } : {})).data
  },

  async getSubscription(tenantId: string): Promise<CustomerSubscription> {
    return (await api.get<CustomerSubscription>(`/admin/tenants/${tenantId}/subscription`)).data
  },

  async setSubscriptionPlan(tenantId: string, plan: string): Promise<CustomerSubscription> {
    return (await api.put<CustomerSubscription>(`/admin/tenants/${tenantId}/subscription/plan`, { plan })).data
  },

  async cancelSubscription(tenantId: string): Promise<CustomerSubscription> {
    return (await api.post<CustomerSubscription>(`/admin/tenants/${tenantId}/subscription/cancel`)).data
  },

  async resumeSubscription(tenantId: string): Promise<CustomerSubscription> {
    return (await api.post<CustomerSubscription>(`/admin/tenants/${tenantId}/subscription/resume`)).data
  },

  async billingHealth(sector: string): Promise<BillingHealth> {
    return (await api.get<BillingHealth>('/admin/billing/health', { params: { vertical_code: sector } })).data
  },

  /** Apply a discount code (voucher) to the customer's subscription, ops-only codes included. */
  async applyVoucher(tenantId: string, code: string): Promise<{ id: string; status: string }> {
    return (await api.post<{ id: string; status: string }>(`/admin/tenants/${tenantId}/vouchers`, { code })).data
  },

  // --- Curriculum V2 Document & Edition CRUD ---

  async updateCurriculumV2Document(
    documentId: string,
    payload: { title?: string; source_uri?: string; checksum?: string | null; is_global?: boolean },
  ): Promise<CurriculumDocument> {
    const res = await api.patch<CurriculumDocument>(
      `/admin/curriculum/v2/documents/${documentId}`,
      payload,
    )
    return res.data
  },

  async deleteCurriculumV2Document(documentId: string): Promise<void> {
    await api.delete(`/admin/curriculum/v2/documents/${documentId}`)
  },

  async updateCurriculumV2Edition(
    editionId: string,
    payload: { title?: string; version_tag?: string; published_year?: number | null; locale?: string },
  ): Promise<CurriculumEdition> {
    const res = await api.patch<CurriculumEdition>(
      `/admin/curriculum/v2/editions/${editionId}`,
      payload,
    )
    return res.data
  },

  async deleteCurriculumV2Edition(editionId: string): Promise<void> {
    await api.delete(`/admin/curriculum/v2/editions/${editionId}`)
  },

  async uploadCurriculumV2Document(
    file: File,
    metadata: { curriculum_code: string; title: string; is_global?: boolean },
  ): Promise<CurriculumDocument> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('curriculum_code', metadata.curriculum_code)
    formData.append('title', metadata.title)
    if (metadata.is_global !== undefined) {
      formData.append('is_global', String(metadata.is_global))
    }
    const res = await api.post<CurriculumDocument>(
      '/admin/curriculum/v2/documents/upload',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return res.data
  },

  async listCurriculumV2Documents(params?: {
    status?: string
    curriculumCode?: string
    limit?: number
  }): Promise<CurriculumDocument[]> {
    const search = new URLSearchParams()
    if (params?.status) search.append('status', params.status)
    if (params?.curriculumCode) search.append('curriculum_code', params.curriculumCode)
    if (params?.limit) search.append('limit', params.limit.toString())
    const suffix = search.toString() ? `?${search.toString()}` : ''
    const res = await api.get<CurriculumDocument[]>(`/admin/curriculum/v2/documents${suffix}`)
    return res.data
  },

  async createCurriculumV2Document(payload: CurriculumDocumentCreatePayload): Promise<CurriculumDocument> {
    const res = await api.post<CurriculumDocument>('/admin/curriculum/v2/documents', payload)
    return res.data
  },

  async listCurriculumV2Editions(params?: { status?: string; limit?: number }): Promise<CurriculumEdition[]> {
    const search = new URLSearchParams()
    if (params?.status) search.append('status', params.status)
    if (params?.limit) search.append('limit', params.limit.toString())
    const suffix = search.toString() ? `?${search.toString()}` : ''
    const res = await api.get<CurriculumEdition[]>(`/admin/curriculum/v2/editions${suffix}`)
    return res.data
  },

  async createCurriculumV2Edition(payload: CurriculumEditionCreatePayload): Promise<CurriculumEdition> {
    const res = await api.post<CurriculumEdition>('/admin/curriculum/v2/editions', {
      curriculum_code: payload.curriculum_code,
      version_tag: payload.version_tag,
      title: payload.title,
      published_year: payload.published_year ?? null,
      locale: payload.locale ?? 'en',
    })
    return res.data
  },

  async runCurriculumV2Extraction(payload: CurriculumExtractionPayload): Promise<CurriculumExtractionSummary> {
    const res = await api.post<CurriculumExtractionSummary>('/admin/curriculum/v2/extract', payload)
    return res.data
  },

  async activateCurriculumV2Edition(
    editionId: string,
    payload?: { generate_embeddings?: boolean }
  ): Promise<CurriculumActivationResult> {
    const res = await api.post<CurriculumActivationResult>(`/admin/curriculum/v2/editions/${editionId}/activate`, {
      generate_embeddings: payload?.generate_embeddings ?? true,
    })
    return res.data
  },

  async runCurriculumV2BatchExtraction(
    payload: CurriculumBatchExtractionPayload,
  ): Promise<CurriculumBatchExtractionResponse> {
    const res = await api.post<CurriculumBatchExtractionResponse>(
      '/admin/curriculum/v2/extract-batch',
      payload,
    )
    return res.data
  },

  async inferCurriculumV2Prerequisites(
    payload: CurriculumPrerequisiteInferPayload
  ): Promise<CurriculumPrerequisiteInferResult> {
    const res = await api.post<CurriculumPrerequisiteInferResult>('/admin/curriculum/v2/infer-prerequisites', {
      edition_id: payload.edition_id,
      target_edition_id: payload.target_edition_id ?? null,
      model_name: payload.model_name ?? null,
    })
    return res.data
  },

  // --- Curriculum Graph Viewer ---

  async getCurriculumV2EditionTree(
    editionId: string,
    params?: { grade?: string },
  ): Promise<CurriculumEditionTreeResponse> {
    const search = new URLSearchParams()
    if (params?.grade) search.append('grade', params.grade)
    const suffix = search.toString() ? `?${search.toString()}` : ''
    const res = await api.get<CurriculumEditionTreeResponse>(
      `/admin/curriculum/v2/editions/${editionId}/tree${suffix}`,
    )
    return res.data
  },

  async getCurriculumV2SubStrandDetail(
    subStrandId: string,
  ): Promise<CurriculumSubStrandDetailResponse> {
    const res = await api.get<CurriculumSubStrandDetailResponse>(
      `/admin/curriculum/v2/sub-strands/${subStrandId}`,
    )
    return res.data
  },

  async getCurriculumV2EditionStats(
    editionId: string,
  ): Promise<CurriculumEditionStatsResponse> {
    const res = await api.get<CurriculumEditionStatsResponse>(
      `/admin/curriculum/v2/editions/${editionId}/stats`,
    )
    return res.data
  },
}

// === Discount Service ===

export const discountService = {
  /** A sector's discount codes: each belongs to one sector. */
  async list(sector: string, status?: string): Promise<Discount[]> {
    const params = new URLSearchParams({ vertical_code: sector })
    if (status) params.append('status', status)
    const res = await api.get<Discount[]>(`/discounts?${params}`)
    return res.data
  },

  async get(id: string): Promise<Discount> {
    const res = await api.get<Discount>(`/discounts/${id}`)
    return res.data
  },

  async create(sector: string, data: DiscountCreatePayload): Promise<Discount> {
    const res = await api.post<Discount>('/discounts', data, { params: { vertical_code: sector } })
    return res.data
  },

  async update(id: string, data: DiscountUpdatePayload): Promise<Discount> {
    const res = await api.patch<Discount>(`/discounts/${id}`, data)
    return res.data
  },
}

// === Verification queue (gated-demo G2) =====================================

export type VerificationStatus =
  | 'pending'
  | 'awaiting_contact'
  | 'in_progress'
  | 'approved'
  | 'rejected'

export type InterestType = 'demo' | 'sales' | 'waitlist'

export type OperatingModel = 'school' | 'owner_managed' | 'agency_managed'

/** How each operating model reads to ops. Agencies get trust accounting. */
export const OPERATING_MODEL_LABELS: Record<OperatingModel, string> = {
  school: 'School',
  owner_managed: 'Owns the properties',
  agency_managed: 'Manages for owners (agency)',
}

/** The models a tenant of this vertical may have; none for other verticals. */
export function operatingModelsFor(verticalCode: string | null | undefined): OperatingModel[] {
  if (verticalCode === 'school') return ['school']
  if (verticalCode === 'rental' || verticalCode === 'property') return ['owner_managed', 'agency_managed']
  return []
}

export interface VerificationSummary {
  id: string
  tenant_id: string | null
  status: VerificationStatus
  level: string
  vertical_code: string | null
  operating_model: OperatingModel | null
  lead_source: string | null
  interest_type: InterestType | null
  estimated_size: string | null
  institution_name: string | null
  registration_number: string | null
  county: string | null
  contact_name: string
  contact_email: string | null
  contact_phone: string
  contact_title: string | null
  submitted_at: string | null
  verified_at: string | null
  rejection_reason: string | null
}

export interface CollectionAccountCheck {
  /** False for a school that collects nothing (CBC-only): approved on a call. */
  collects: boolean
  /** Approval needs a call to a number staff found themselves. */
  needs_call: boolean
  connected?: boolean
  bank?: string | null
  number?: string | null
  /** Who the account is registered to, as the bank confirmed it. */
  holder_name?: string | null
  /** What the organisation typed when asking us to connect it. */
  requested_name?: string | null
  organisation_name?: string
  names_match?: boolean
}

export interface VerificationDetail extends VerificationSummary {
  physical_address: string | null
  has_document: boolean
  /** For an organisation in setup: what approval is checked against. */
  collection_account: CollectionAccountCheck | null
  verification_method: string | null
  document_url: string | null
  lead_notes: string | null
  lead_attribution: Record<string, string | undefined> | null
}

export interface VerificationListResponse {
  items: VerificationSummary[]
  total: number
  limit: number
  offset: number
}

export interface VerificationListFilters {
  status?: VerificationStatus[]
  limit?: number
  offset?: number
}

export const verificationService = {
  /** A short-lived link to the certificate a school sent. */
  async document(id: string): Promise<{ url: string; filename: string; content_type: string }> {
    return (await api.get(`/admin/verifications/${id}/document`)).data
  },

  async list(sector: string, filters: VerificationListFilters = {}): Promise<VerificationListResponse> {
    const params = new URLSearchParams({ vertical_code: sector })
    if (filters.status) {
      for (const s of filters.status) params.append('status', s)
    }
    if (filters.limit !== undefined) params.append('limit', String(filters.limit))
    if (filters.offset !== undefined) params.append('offset', String(filters.offset))
    const url = `/admin/verifications?${params}`
    const res = await api.get<VerificationListResponse>(url)
    return res.data
  },

  async get(id: string): Promise<VerificationDetail> {
    const res = await api.get<VerificationDetail>(`/admin/verifications/${id}`)
    return res.data
  },

  async approve(
    id: string,
    note?: string,
    operatingModel?: OperatingModel | null,
    call?: { number: string; source: string } | null,
  ): Promise<VerificationDetail> {
    const res = await api.post<VerificationDetail>(
      `/admin/verifications/${id}/approve`,
      {
        note: note ?? null,
        operating_model: operatingModel ?? null,
        called_number: call?.number || null,
        called_source: call?.source || null,
      },
    )
    return res.data
  },

  async reject(id: string, reason: string): Promise<VerificationDetail> {
    const res = await api.post<VerificationDetail>(
      `/admin/verifications/${id}/reject`,
      { reason },
    )
    return res.data
  },

  // === Demo invites (gated-demo G3) ===

  async sendInvite(verificationId: string): Promise<DemoInviteSummary> {
    const res = await api.post<DemoInviteSummary>(
      `/admin/verifications/${verificationId}/send-invite`,
    )
    return res.data
  },

  async getInvite(verificationId: string): Promise<DemoInviteSummary | null> {
    const res = await api.get<DemoInviteSummary | null>(
      `/admin/verifications/${verificationId}/invite`,
    )
    return res.data
  },

  async revokeInvite(inviteId: string): Promise<DemoInviteSummary> {
    const res = await api.post<DemoInviteSummary>(
      `/admin/demo-invites/${inviteId}/revoke`,
    )
    return res.data
  },
}

export type DemoInviteStatus = 'pending' | 'redeemed' | 'expired' | 'revoked'

export interface DemoInviteSummary {
  id: string
  verification_id: string
  email: string
  status: DemoInviteStatus
  expires_at: string
  sent_at: string
  last_sent_at: string
  send_count: number
  delivery_status: string
  delivery_error: string | null
  redeemed_at: string | null
  tenant_id: string | null
  revoked_at: string | null
}
